import { useCallback, useEffect, useMemo, useState } from 'react'
import { releaseNotesApi } from '@/lib/api/release-notes.api'
import {
  getReleaseNotesLastVisitedAt,
  getReleaseNotesVisitStorageKey,
  hasUnseenRelease,
  markReleaseNotesVisited,
  RELEASE_NOTES_VISITED_EVENT,
} from '@/lib/release-notes-visit'
import { useQuery } from '@tanstack/react-query'

export function useReleaseNotesUnread(userId?: string | null) {
  const storageKey = useMemo(
    () => getReleaseNotesVisitStorageKey(userId),
    [userId],
  )
  const [lastVisitedAt, setLastVisitedAt] = useState<string | null>(() =>
    getReleaseNotesLastVisitedAt(userId),
  )
  const latestQuery = useQuery({
    queryKey: ['release-notes', 'latest'],
    queryFn: () => releaseNotesApi.list(1, 1),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
  })

  useEffect(() => {
    setLastVisitedAt(getReleaseNotesLastVisitedAt(userId))
  }, [userId])

  useEffect(() => {
    const handleVisited = (event: Event) => {
      const detail = (
        event as CustomEvent<{ storageKey: string; visitedAt: string }>
      ).detail
      if (detail?.storageKey === storageKey) {
        setLastVisitedAt(detail.visitedAt)
      }
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) setLastVisitedAt(event.newValue)
    }

    window.addEventListener(RELEASE_NOTES_VISITED_EVENT, handleVisited)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener(RELEASE_NOTES_VISITED_EVENT, handleVisited)
      window.removeEventListener('storage', handleStorage)
    }
  }, [storageKey])

  const markVisited = useCallback(() => {
    const visitedAt = markReleaseNotesVisited(userId)
    if (visitedAt) setLastVisitedAt(visitedAt)
  }, [userId])

  const latestPublishedAt = Array.isArray(latestQuery.data?.items)
    ? latestQuery.data.items[0]?.publishedAt
    : undefined

  return {
    hasUnread: hasUnseenRelease(latestPublishedAt, lastVisitedAt),
    markVisited,
  }
}
